import { SystemAlarmListComponent } from '@alarm';
import { SystemDashboardComponent, SystemDashboardDropdownService, SystemDashboardNaviExtension } from '@dashboards';
import { SystemsWebPictureNaviExtensionService } from '@datapoints';
import { SystemFileDetails, SystemFileListComponent } from '@files';
import { __asyncI18n } from '@localisation';
import { SubnaviDropDownService } from '@navigation';
import { CanActivateWithCurrentTenant, SystemPlantListComponent } from '@plants';
import { RoutePrivilegeNames } from '@route-privilege-names';
import { DummySiteComponent } from '@site-info';
import { SystemAlarmIconComponent, SystemManageUsersAccessService, SystemNotificationsAccessService, SystemOperatingNaviExtension, SystemSettingsAccessService } from '@systems';

export const SystemOperatingRoutes = [{
    path: ':systemid',
    data: {
        title: ':systemid | systemIdToName',
        dontShowInSubNavigation: true,
        dontShowFavourites: true,
        breadCrumbDropDown: SubnaviDropDownService,
        favouritesRouteTitle: '[SiteName]',
        //lastSubNaviLevel : true,
        redirectTo: [
            'Operating/Systems/:systemid/SystemDashboard/*firstChild',
            'Operating/Systems/:systemid/Plants',
            'Operating/Systems/:systemid/WebPicture',
            'Operating/Systems/:systemid/Files'
        ]
    },
    children: [
        {
            path: '',
            canActivate: [CanActivateWithCurrentTenant],
            component: DummySiteComponent,
            data: {
                title: ':systemid | systemIdToName',
                dontShowInSubNavigation: true,
                dontShowFavourites: true,
                favouritesRouteCaption: '[SiteName]',
                redirectTo: [
                    'Operating/Systems/:systemid/SystemDashboard/*firstChild',
                    'Operating/Systems/:systemid/Plants',
                    'Operating/Systems/:systemid/WebPicture',
                    'Operating/Systems/:systemid/Files'
                ]
            }
        },
        {
            path: 'SystemDashboard',
            data: {
                title: __asyncI18n('System dashboard', 'subnavi.operating.systemdashboard'),
                hideWithoutChildren: true,
                dynamicChildren: SystemDashboardNaviExtension,
                showChildrenAsSecondLevel: true,
                breadCrumbDropDown: SystemDashboardDropdownService,
                redirectTo: 'Operating/Systems/:systemid/SystemDashboard/*firstChild',
                testId: 'sidenavigation.link.systemdashboard',
                privileges: [RoutePrivilegeNames.SystemDashboard]
            },
            children: [
                {
                    path: ':dashboardid',
                    data: {
                        title: ':dashboardid | systemDashboardIdToName',
                        redirectTo: 'Operating',
                        dontShowInSubNavigation: true,
                        hideWithoutChildren: true,
                        favouritesCaption: '[SystemDashboardName]'
                    },
                    children: [
                        {
                            path: '',
                            component: SystemDashboardComponent,
                            data: {
                                title: ':dashboardid | systemDashboardIdToName',
                                dontShowInSubNavigation: true,
                                hideWithoutChildren: true,
                                favouritesCaption: '[SystemDashboardName]'
                            }
                        },
                        {
                            path: 'Trend',
                            data: {
                                title: __asyncI18n('Trend', 'subnavi.operating.systemdashboard.trend'),
                                dontShowInSubNavigation: true,
                                showSubNaviLevel: 3,
                                dontShowInBreadCrumb: true
                            },
                            children: [
                                {
                                    path: ':tileid',
                                    loadChildren: () => import('../modules/viewnodes-module/index').then(m => m.ViewNodesModule),
                                    data: {
                                        title: ':tileid',
                                        dontShowInSubNavigation: true,
                                        lazyRedirectTo: 'Operating/Plants/:systemid/SystemDashboard/:dashboardid/Trend/:tileid/SystemView',
                                        showSubNaviLevel: 4
                                    }
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            path: 'Plants',
            component: SystemPlantListComponent,
            data: {
                title: __asyncI18n('Plants', 'subnavi.operating.systems.plants'),
                testId: 'sidenavigation.link.systemplants',
                privileges: [],
                dontShowFavourites: true,
                favouritesType: 'sites',
                favouritesTitle: '[PageName]',
            }
        },
        {
            path: 'Alarms',
            component: SystemAlarmListComponent,
            data: {
                title: __asyncI18n('Alarms', 'subnavi.operating.systemalarms'),
                statusService: {
                    component: SystemAlarmIconComponent,
                    parameter: {
                        systemId: ':systemid'
                    }
                },
                testId: 'sidenavigation.link.systemalarms',
                privileges: [
                    RoutePrivilegeNames.SystemAlarm
                ]
            }
        },
        {
            path: 'WebPicture',
            data: {
                title: __asyncI18n('Web graphic', 'subnavi.operating.systems.webpicture'),
                hideWithoutChildren: true,
                dynamicChildren: SystemsWebPictureNaviExtensionService,
                showChildrenAsSecondLevel: true,
                redirectTo: 'Operating/Systems/:systemid/WebPicture/*firstChild',
                testId: 'sidenavigation.link.webpicture',
                privileges: [
                    RoutePrivilegeNames.SystemGraphic
                ]

            },
            children: [
                {
                    path: ':webpictureid',
                    data: {
                        title: ':webpictureid',
                        redirectTo: 'Operating/Systems/:systemid/WebPicture/:webpictureid/SystemView',
                        lazyRedirectTo: 'Operating/Systems/:systemid/WebPicture/:webpictureid/SystemView',
                        dontShowInSubNavigation: true,
                        dontShowInBreadCrumb: true,
                        hideWithoutChildren: true,
                    },
                    children: [
                        {
                            path: 'SystemView',
                            component: DummySiteComponent,
                            data: {
                                title: 'SystemView',
                                redirectTo: 'Operating/Systems/:systemid/WebPicture/*firstChild',
                                dontShowInSubNavigation: true,
                                dontShowInBreadCrumb: true,
                                showSubNaviLevel : 4,
                                favouritesType: 'webpicture'
                            }
                        }
                    ]
                }
            ]
        },
        {
            path: 'Files',
            data: {
                title: __asyncI18n('Files', 'subnavi.operating.systems.files'),
                testId: 'sidenavigation.link.systemfiles',
                privileges: [
                    RoutePrivilegeNames.SystemFileAccess,
                    RoutePrivilegeNames.SystemFileManage
                ]
            },
            children: [
                {
                    path: '',
                    component: SystemFileListComponent,
                    data: {
                        title: __asyncI18n('Files', 'subnavi.operating.systems.files'),
                        showSubNaviLevel: 3
                    }
                },
                {
                    path: 'Add',
                    data: {
                        title: __asyncI18n('Add file', 'subnavi.operating.systems.file.add'),
                        showSubNaviLevel: 3,
                    },
                    children: [
                        {
                            path: '',
                            component: SystemFileDetails,
                            data: {
                                title: __asyncI18n('Add file', 'subnavi.operating.systems.file.add'),
                                showSubNaviLevel: 3
                            }
                        },
                        {
                            path: ':type',
                            component: SystemFileDetails,
                            data: {
                                title: __asyncI18n('Add Parameter file', 'subnavi.operating.systems.file.add.parameter'),
                                showSubNaviLevel: 3,
                                dontShowInBreadCrumb: true

                            }
                        }
                    ]
                },
                {
                    path: ':fileid',
                    data: {
                        title: ':fileid | systemFileIdToName',
                        dontShowInSubNavigation: true,
                        showSubNaviLevel: 3,
                        favouritesCaption: '[ParentPageName] / [PageName]',
                    },
                    children: [
                        {
                            path: '',
                            component: SystemFileDetails,
                            data: {
                                title: ':fileid | systemFileIdToName',
                                dontShowInSubNavigation: true,
                                showSubNaviLevel: 3,
                                favouritesCaption: '[ParentPageName] / [PageName]'
                            }
                        }
                    ]
                }
            ]
        },
        {
            path: 'SystemSettings',
            component: DummySiteComponent,
            canActivate: [
                SystemSettingsAccessService
            ],
            data: {
                redirectTo: '/Administration/Systems/:systemid/Settings',
                title: __asyncI18n('System settings', 'subnavi.administration.system.settings'),
                testId: 'sidenavigation.link.systemsettings',
                dynamicSiblings: SystemOperatingNaviExtension
            }
        },
        {
            path: 'SystemUsers',
            component: DummySiteComponent,
            canActivate: [
                SystemManageUsersAccessService
            ],
            data: {
                redirectTo: '/Administration/Systems/:systemid/Users',
                title: __asyncI18n('System users', 'subnavi.administration.system.system.users'),
                testId: 'sidenavigation.link.systemusers',
                dynamicSiblings: SystemOperatingNaviExtension
            }
        },
        {
            path: 'SystemNotifications',
            component: DummySiteComponent,
            canActivate: [ SystemNotificationsAccessService ],
            data: {
                redirectTo: 'Administration/Systems/:systemid/SystemNotifications',
                title: __asyncI18n('System notifications', 'subnavi.administration.system.systemnotifications'),
                testId: 'sidenavigation.link.systemnotifications'
            }
        }
    ]
}]